import { AlertDialog as BaseAlertDialog } from "@base-ui/react/alert-dialog";
import * as React from "react";

import { RobotoButton } from "../RobotoButton";

import styles from "./ConfirmDialog.module.css";

export interface ConfirmDialogProps {
  /** Controlled open state. The dialog closes only through `onOpenChange`. */
  open: boolean;
  /**
   * Fired when the user cancels: the Cancel button or Escape. Confirming does
   * not fire it — the caller closes once its action has settled, so a failure
   * can leave the dialog up with its error.
   */
  onOpenChange: (open: boolean) => void;
  /** The question, rendered as the heading and the dialog's accessible name. */
  title: string;
  /** One line under the title: what confirming does, or what cannot be undone. */
  description?: string;
  /** Extra content between the description and the buttons — an error, say. */
  children?: React.ReactNode;
  /** Label for the confirm button. Name the action, not "Yes" or "OK". */
  confirmLabel: string;
  /** @default "Cancel" */
  cancelLabel?: string;
  /**
   * Renders the confirm as the dangerous choice — solid red against an
   * outlined cancel — so the two never read alike.
   */
  destructive?: boolean;
  /** Shows a spinner on the confirm and disables both buttons while true. */
  pending?: boolean;
  onConfirm: () => void;
}

/**
 * A modal confirmation with one question and two answers, built on Base UI's
 * AlertDialog and styled to match the `Popover` primitive.
 *
 * Deliberately thin: the caller owns the action and its outcome. Pass
 * `pending` while it runs and call `onOpenChange(false)` once it is done, or
 * render the error as `children` and leave the dialog open.
 *
 * Focus lands on Cancel, so a stray Enter never confirms. Escape cancels; a
 * click on the backdrop does not, since the question needs an answer. Escape
 * is stopped from reaching an enclosing popover, so a confirmation opened
 * from a popover menu closes alone.
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={pending !== null}
 *   onOpenChange={(next) => !next && setPending(null)}
 *   title={`Delete ${pending.name}?`}
 *   description="This action cannot be undone."
 *   confirmLabel="Delete"
 *   destructive
 *   pending={isDeleting}
 *   onConfirm={() => void remove(pending)}
 * />
 * ```
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  pending = false,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <BaseAlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <BaseAlertDialog.Portal>
        <BaseAlertDialog.Backdrop className={styles.backdrop} />
        <BaseAlertDialog.Viewport className={styles.viewport}>
          <BaseAlertDialog.Popup className={styles.popup}>
            <BaseAlertDialog.Title className={styles.title}>
              {title}
            </BaseAlertDialog.Title>
            {description !== undefined && (
              <BaseAlertDialog.Description className={styles.description}>
                {description}
              </BaseAlertDialog.Description>
            )}
            {children}
            <div className={styles.actions}>
              <RobotoButton
                variant="secondary"
                disabled={pending}
                onClick={() => onOpenChange(false)}
              >
                {cancelLabel}
              </RobotoButton>
              <RobotoButton
                variant={destructive ? "destructive" : "primary"}
                loading={pending}
                onClick={onConfirm}
              >
                {confirmLabel}
              </RobotoButton>
            </div>
          </BaseAlertDialog.Popup>
        </BaseAlertDialog.Viewport>
      </BaseAlertDialog.Portal>
    </BaseAlertDialog.Root>
  );
}
